import AttachMoney from '@hc-frontend/core-icons/kit/attach-money';
import { CurrencyInput, ErrorMessage, Select } from '@hc-frontend/core-ui';
import { FormEvent, useState } from 'react';
import { z } from 'zod';

import {
  HOUSEHOLD_INCOME,
  HOUSEHOLD_INCOME_SCHEMA,
  HOUSEHOLD_SIZE,
  HOUSEHOLD_SIZE_SCHEMA,
} from './constants';
import { FormButtons } from './form-buttons';
import { useSubsidyCalculator } from './provider';
import { currencyToNumber, numberToCurrency } from './utils';

const FormSchema = z.object({
  householdSize: HOUSEHOLD_SIZE_SCHEMA,
  householdIncome: HOUSEHOLD_INCOME_SCHEMA,
});

export function HouseholdStep() {
  const { setStep } = useSubsidyCalculator();
  const { answers, setAnswer } = useSubsidyCalculator();

  const [householdSize, setHouseholdSize] = useState<string>(
    String(answers[HOUSEHOLD_SIZE] ?? 1),
  );

  const [householdIncome, setHouseholdIncome] = useState<string>(
    numberToCurrency(answers[HOUSEHOLD_INCOME] ?? 0),
  );

  const [householdSizeError, setHouseholdSizeError] = useState<string | null>(
    null,
  );
  const [householdIncomeError, setHouseholdIncomeError] = useState<
    string | null
  >(null);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setHouseholdSizeError(null);
    setHouseholdIncomeError(null);

    const result = FormSchema.safeParse({
      householdSize: Number(householdSize),
      householdIncome: currencyToNumber(householdIncome),
    });

    if (result.success) {
      setAnswer(HOUSEHOLD_SIZE, result.data.householdSize);
      setAnswer(HOUSEHOLD_INCOME, result.data.householdIncome);
      setStep((v) => v + 1);
    } else {
      const fields = result.error.format();

      if (fields.householdSize) {
        setHouseholdSizeError('Please select a valid household size.');
      }

      if (fields.householdIncome) {
        setHouseholdIncomeError('Please enter a valid household income.');
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="grid grid-cols-1 gap-16-16">
        <Select
          label="Household Size"
          className="max-w-none animate-in fade-in slide-in-from-bottom-8-8 duration-1000 fill-mode-both"
          name={HOUSEHOLD_SIZE}
          value={householdSize}
          onChange={(e) => setHouseholdSize(e.target.value)}
          options={['1', '2', '3', '4', '5'].map((item) => ({
            id: item,
            label: item === '5' ? '5 or more' : item,
          }))}
          helpText="Include yourself, spouse, and dependents who want to enroll in coverage."
          errorText={
            householdSizeError ? (
              <ErrorMessage>{householdSizeError}</ErrorMessage>
            ) : undefined
          }
        />

        <CurrencyInput
          label="Household Income (Annual)"
          className="max-w-none animate-in fade-in slide-in-from-bottom-8-8 duration-1000 delay-150 fill-mode-both"
          placeholder="Enter amount"
          name={HOUSEHOLD_INCOME}
          initialValue={householdIncome}
          onChange={(value) => setHouseholdIncome(value)}
          icon={
            <div>
              <AttachMoney height="24px" width="24px" />
            </div>
          }
          helpText="Enter your expected total household income for 2025 (before taxes)"
          errorText={
            householdIncomeError ? (
              <ErrorMessage>{householdIncomeError}</ErrorMessage>
            ) : undefined
          }
        />
      </div>

      <FormButtons
        showBack
        className="animate-in fade-in slide-in-from-bottom-8-8 duration-1000 delay-300 fill-mode-both"
      />
    </form>
  );
}
